import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { Form, Formik } from 'formik';
import { Card, CardActions, CardContent, useEventCallback } from '@mui/material';
import FormDocument from 'front-office/components/residency-permit/edit/form';
import { DocumentFormVM } from 'front-office/view-models/document/document-form';
import { validationSchema } from 'front-office/constants/document-validation-schema';
import { initialValues } from 'front-office/constants/document-initial-values';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useSearchParams } from 'react-router-dom';
import { PmiButton } from 'shell/components/shared/button';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ResponseCertificateGeneric } from 'shell/models/response-certificate-generic';
import { HttpResponse } from 'shell/models/http-response';
// import { openPdf } from 'shell/utils/open-pdf';
import { toast } from 'react-toastify';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResidencyPermitEditCrumbs } from 'front-office/constants/residency-permit--edit-crumbs';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { AxiosResponse } from 'axios';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import ErrorModal from 'front-office/components/residency-permit/edit/error-modal';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { DateTime } from 'luxon';
import { FamilyCoexistenceVM } from 'shell/view-models/family-coexistence';
import { CitizenshipVM } from 'shell/view-models/citizenship';
import { AccordionPersonResidencyPermitForm } from 'shell/view-models/accordion-person-residency-permit-form';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { transformToChoiceDate } from 'shell/components/shared/choice-date/functions';

const ResidencyPermitEdit = (): React.ReactElement => {
  const [queryParams] = useSearchParams();
  // query params
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || undefined;
  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';

  const navigate = useNavigate();

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  // person
  const [anprResponse] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    AccordionPersonResidencyPermitForm
  >(`${__PERSON_API_URL__}/ricerca-persona/rilascio-permesso-di-soggiorno`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {
        criteriRicerca: {
          codiceFiscale: fiscalCode || undefined,
          idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
        },
        datiRichiesta: {
          ricercaNazionale: nationalSearch ? '1' : '0',
          dataRiferimentoRichiesta:
            filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
            undefined,
          comuneResidenza:
            filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
            filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
            filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
            filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
              ? {
                  siglaProvinciaIstat:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.province ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.province ||
                    undefined,
                  codiceIstat:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                    undefined,
                  nomeComune:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                      ?.description ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                      ?.description ||
                    undefined,
                }
              : undefined,
        },
      },
    },
    transformer: (response) => {
      const subjects = response.data.payload?.risposta3002OK?.listaSoggetti.datiSoggetto || [];
      const [subject] = subjects;
      const residenceArray = subject.residenza || [];

      let state = '';

      // state check for object
      if (residenceArray[0]?.indirizzo?.toponimo) {
        state = 'Residente';
      }

      if (subject.generalita?.soggettoAIRE && subject.generalita?.soggettoAIRE === 'S') {
        state = 'Iscritto AIRE';
      }

      if (subject.datiCancellazione && subject.datiCancellazione?.descrizioneMotivoCancellazione) {
        state = `${subject.datiCancellazione.descrizioneMotivoCancellazione}`;
      }

      const person: AccordionPersonForm = {
        surname: subject.generalita?.cognome,
        name: subject.generalita?.nome,
        withoutName: !Boolean(subject.generalita?.nome),
        gender: subject.generalita?.sesso,
        withoutSurname: !Boolean(subject.generalita?.cognome),
        fiscalCode: subject.generalita?.codiceFiscale?.codFiscale,
        state,
        warning: response.data.payload.warning,
      };

      const generality: AccordionGeneralityForm = {
        validityFiscalCodeId: subject.generalita?.codiceFiscale?.validitaCF,
        gender: subject.generalita?.sesso,
        birthDate: subject.generalita?.dataNascita
          ? DateTime.fromISO(subject.generalita.dataNascita || '')
          : null,
        isForeign: subject.generalita?.luogoNascita?.localita ? true : false,
        birthChoiceDate: transformToChoiceDate(
          subject?.generalita?.senzaGiorno || '',
          subject?.generalita?.senzaGiornoMese || ''
        ),
        italian: {
          municipality: {
            description: subject?.generalita?.luogoNascita?.comune?.nomeComune || '',
            province: subject?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            provinceCode:
              subject?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || undefined,
            id: subject?.generalita?.luogoNascita?.comune?.codiceIstat || undefined,
          },
          location: subject?.generalita?.luogoNascita?.comune?.descrizioneLocalita || undefined,
          province: subject?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
        },
        foreign: {
          state: {
            id: subject.generalita?.luogoNascita?.localita?.codiceStato || '',
            value: subject.generalita?.luogoNascita?.localita?.descrizioneStato || '',
          },
          municipality: {
            description: subject.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
            province: subject.generalita?.luogoNascita?.localita?.provinciaContea || '',
          },
        },
      };

      const citizenshipResidencyPermit: Array<CitizenshipVM> | undefined = subject.cittadinanza
        ? subject.cittadinanza.map((citizen) => ({
            code: citizen.codiceStato || '',
            citizenship: citizen.descrizioneStato || '',
            note: citizen.noteStato || '',
            validityDate: citizen.dataValidita
              ? DateTime.fromISO(citizen.dataValidita).toFormat('dd/MM/yyyy')
              : '',
          }))
        : undefined;

      const family: FamilyCoexistenceVM = {
        legameSoggetto: {
          codiceLegame: subject.legameSoggetto?.codiceLegame,
        },
      };

      return {
        person,
        generality,
        citizenshipResidencyPermit,
        family,
        residencePermit: subject.permessoSoggiorno,
      };
    },
  });

  const [responseResidency, getResidencyPermit] = useHttpFetch<
    HttpResponse<ResponseCertificateGeneric & ResponseAnprSaveData>,
    HttpResponse<ResponseCertificateGeneric> & SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/registrazione-permesso-di-soggiorno`, {
    onBootstrap: false,
    options: {
      method: 'POST',
    },
    transformer: (
      response: AxiosResponse<HttpResponse<ResponseCertificateGeneric & ResponseAnprSaveData>>
    ): HttpResponse<ResponseCertificateGeneric> & SaveDataAnpr => {
      return {
        ...response.data,
        warnings: (response.data.payload?.rispostaComune?.listaAnomalie?.errore || []).map(
          (warning) => ({
            id: warning.codiceErroreAnomalia,
            value: warning.testoErroreAnomalia,
          })
        ),
        errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
          id: error.codiceErroreAnomalia,
          value: error.testoErroreAnomalia,
        })),
      };
    },
  });

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });
  const [openModal, setOpenModal] = useState(false);

  const castedAnprData = responseResidency.data as HttpResponse<ResponseCertificateGeneric> &
    SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  useEffect(() => {
    if ((anprResponse.data as AccordionPersonResidencyPermitForm)?.person?.warning) {
      // setOpenModal(true);
    }
  }, [anprResponse.data]);

  const onConfirmation = useEventCallback(async (values: DocumentFormVM) => {
    const data = {
      file: values.file ? (await blobToBase64(values.file))?.split(',')[1] : undefined,
      nomeFile: values.file?.name || undefined,
      datiControllo: {
        tipoMutazione: ['04'],
      },
      generalitaPerRicerca: {
        idSchedaSoggettoANPR,
      },
      permessoSoggiorno: {
        numeroSoggiorno: values?.numberAllowed || undefined,
        tipoSoggiorno: values?.type || undefined,
        noteSoggiorno: values?.note,
        dataRilascio: values?.dateRelease ? values.dateRelease.toFormat('yyyy-MM-dd') : undefined,
        dataScadenza: values?.dateExpiration
          ? values.dateExpiration.toFormat('yyyy-MM-dd')
          : undefined,
        questuraRilascioSoggiorno:
          values?.questuraRelease || values.municipality?.description || undefined,
        numeroPassaporto: values?.passportNumber || undefined,
        comune: {
          nomeComune: values?.municipality?.description || undefined,
          codiceIstat: values?.municipality?.id || undefined,
          siglaProvinciaIstat: values?.municipality?.province || values?.province || undefined,
        },
      },
      dataDichiarazione: values?.dateDeclaration
        ? values.dateDeclaration.toFormat('yyyy-MM-dd')
        : undefined,
    };

    (
      getResidencyPermit({ data }) as ResponsePromise<
        HttpResponse<ResponseCertificateGeneric & ResponseAnprSaveData>,
        HttpResponse<ResponseCertificateGeneric> & SaveDataAnpr
      >
    ).then((response) => {
      const responseCasted = response as HttpResponse<ResponseCertificateGeneric> & SaveDataAnpr;

      const pdfCertificate = responseCasted?.payload?.contenutoPdfCertificato;

      setVisibility({ error: undefined, warning: undefined });

      if (responseCasted?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }

      if (pdfCertificate) {
        // open pdf residency permit
        // openPdf(pdfCertificate);
        setPdfModal({ data: pdfCertificate, name: 'Permesso_Soggiorno', open: true });
      }

      toast.success('Operazione eseguita con successo');
    });
  });

  const initialValuesForm: DocumentFormVM | undefined = useMemo(() => {
    if (anprResponse.isLoading || anprResponse.pristine) {
      return undefined;
    }

    const data = anprResponse.data as AccordionPersonResidencyPermitForm;

    return {
      ...initialValues,
      oldResidencyPermit: data?.residencePermit
        ? {
            number: data?.residencePermit?.numeroSoggiorno || '',
            type: data?.residencePermit?.tipoSoggiorno || '',
            releaseDate: data.residencePermit?.dataRilascio
              ? DateTime.fromISO(data.residencePermit?.dataRilascio)
              : null,
            expirationDate: data.residencePermit?.dataScadenza
              ? DateTime.fromISO(data.residencePermit?.dataScadenza)
              : null,
            municipality: data.residencePermit?.comune
              ? {
                  id: data.residencePermit?.comune?.codiceIstat || '',
                  description: data.residencePermit?.comune?.nomeComune || '',
                  province: data.residencePermit?.comune?.siglaProvinciaIstat || '',
                }
              : null,
            note: data.residencePermit?.noteSoggiorno || '',
            policeForceRelease: data.residencePermit?.questuraRilascioSoggiorno || '',
          }
        : null,
      person: data?.person,
      familyCoexistence: data?.family?.legameSoggetto?.codiceLegame
        ? {
            relationship: data?.family?.legameSoggetto?.codiceLegame,
          }
        : undefined,
      generality: data?.generality,
      citizenship: (data?.citizenshipResidencyPermit || []).map((element) => {
        return element;
      }),
    };
  }, [initialValues, anprResponse]);

  return !initialValuesForm ? (
    <></>
  ) : (
    <Fragment>
      <Breadcrumbs items={ResidencyPermitEditCrumbs} base="sportello" />
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValuesForm}
        enableReinitialize
        onSubmit={(values: DocumentFormVM) => {
          onConfirmation(values);
        }}
        validateOnChange
        validateOnBlur
        validateOnMount
      >
        <Form>
          <Card raised className="pmi-my-3">
            <CardContent style={{ padding: '24px' }}>
              <SearchMessages
                type={MessageType.Error}
                title={errorMessage.title || ''}
                messages={errorMessage.messages || []}
                visibility={errorMessage.visibility || false}
                onChangeVisibility={onVisibility}
              />
              <SearchMessages
                type={MessageType.Warning}
                title={
                  castedAnprData?.warnings?.length === 1
                    ? `C'è 1 warning`
                    : `Ci sono ${castedAnprData?.warnings?.length} warning`
                }
                messages={castedAnprData?.warnings || []}
                visibility={
                  visibility.warning !== undefined
                    ? visibility.warning
                    : (castedAnprData?.warnings || [])?.length > 0
                }
                onChangeVisibility={onVisibility}
              />
              <FormDocument />
            </CardContent>
            <CardActions style={{ padding: '24px 24px 16px 24px' }}>
              <PmiButton variant="contained" color="primary" type="submit">
                Conferma
              </PmiButton>
              <PmiButton
                variant="outlined"
                color="cdmBrand"
                onClick={() => {
                  navigate('/sportello');
                }}
              >
                Annulla
              </PmiButton>
            </CardActions>
          </Card>
        </Form>
      </Formik>
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Stampa Permesso Soggiorno"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
          // go back
          navigate('/sportello/rilascioPDS');
        }}
      />
      <ErrorModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
        }}
        message={initialValuesForm.person?.warning || ''}
      />
    </Fragment>
  );
};

ResidencyPermitEdit.displayName = 'Residency Permit Edit page';

export default ResidencyPermitEdit;
